import React from "react";
import { Pressable, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";
import tw from "twrnc";

import Text from "@/components/shared/AppText";
import AppTitle from "@/components/shared/AppTitle";
import AppDatePicker from "@/components/shared/AppDatePicker";
import TimezonePicker from "@/components/shared/TimezonePicker";
import MultiDaySelector from "@/components/shared/MultiDaySelector";
import { TextLink } from "@/components/shared/TextLink";
import {
  QUESTION,
  setWhen,
  whenErrors,
} from "@/components/screens/OnBoarding/eventWhen";
import { t } from "@/localization";

// Onboarding's "when" step. The date and the start time start empty: a
// pre-filled "now" was kept by 45% of new hosts and almost none of those
// events happened then (see audits / Oct 2026 now-dated events study).

const INK = "#2E3F52";
const SLATE = "#3f5b7a";
const BRAND = "#F84123";

const Chip = ({ label, selected, onPress, icon }) => (
  <Pressable
    onPress={onPress}
    accessibilityRole="button"
    accessibilityState={{ selected }}
    style={tw`flex-row items-center px-4 h-[44px] mr-2 mb-2 rounded-full border ${
      selected ? `bg-[${SLATE}] border-[${SLATE}]` : "bg-white border-gray-300"
    }`}
  >
    {icon && (
      <Ionicons
        name={icon}
        size={16}
        color={selected ? "#fff" : SLATE}
        style={tw`mr-1.5`}
      />
    )}
    <Text
      style={tw`text-base ${
        selected ? "text-white font-bold" : `text-[${INK}]`
      }`}
    >
      {label}
    </Text>
  </Pressable>
);

// The field's label, with what's missing next to it after Continue.
const FieldLabel = ({ children, error }) => (
  <View style={tw`flex-row items-baseline flex-wrap mb-2`}>
    <Text style={tw`text-xs font-bold tracking-wide text-[${SLATE}]`}>
      {children}
    </Text>
    {!!error && (
      <Text style={tw`ml-2 text-xs text-[${BRAND}]`} accessibilityRole="alert">
        {error}
      </Text>
    )}
  </View>
);

export const EventWhenForm = ({ f, onNoDate, now = moment() }) => {
  const { values } = f;
  const type = values.event_type || "other";
  const today = now.clone().format("YYYY-MM-DD");
  const tomorrow = now.clone().add(1, "day").format("YYYY-MM-DD");
  const date = values.event_date_local;
  const otherDate = !!date && date !== today && date !== tomorrow;
  // Shown once Continue was pressed, and kept up to date as the host picks.
  const errors = f.status?.whenTried ? whenErrors(values, now) : {};

  const asDate = (ymd, hm = "12:00") =>
    moment(`${ymd} ${hm}`, "YYYY-MM-DD HH:mm").toDate();

  return (
    <>
      <AppTitle
        text={QUESTION[type] || QUESTION.other}
        style={tw`mt-10 mb-2`}
      />
      <Text style={tw`mb-5`}>
        {t("We’ll remind your guests before it starts.")}
      </Text>

      <FieldLabel error={errors.date}>{t("DATE")}</FieldLabel>
      <View style={tw`flex-row flex-wrap`}>
        <Chip
          label={t("Today")}
          selected={date === today}
          onPress={() => setWhen(f, { date: today, quick: "today" })}
        />
        <Chip
          label={t("Tomorrow")}
          selected={date === tomorrow}
          onPress={() => setWhen(f, { date: tomorrow, quick: "tomorrow" })}
        />
        <AppDatePicker
          mode="date"
          trigger="chip"
          selected={otherDate ? asDate(date) : null}
          selectedValue={otherDate ? asDate(date) : null}
          minimumDate={now.clone().startOf("day").toDate()}
          onChange={(value) =>
            setWhen(f, {
              date: moment(value).format("YYYY-MM-DD"),
              quick: "calendar",
            })
          }
          placeholderText={t("Other date")}
        />
      </View>

      <View style={tw`h-2`} />
      <FieldLabel error={errors.time}>{t("START TIME")}</FieldLabel>
      <AppDatePicker
        mode="time"
        selected={
          values.event_time_local
            ? asDate(date || today, values.event_time_local)
            : null
        }
        selectedValue={
          values.event_time_local
            ? asDate(date || today, values.event_time_local)
            : null
        }
        onChange={(value) =>
          setWhen(f, { time: moment(value).format("HH:mm") })
        }
        placeholderText={t("Select the start time")}
        iconName="time-outline"
      />

      <View style={tw`flex-row flex-wrap items-center justify-between -mt-2`}>
        <MultiDaySelector f={f} questionStyle={tw`text-left`} />
        <TimezonePicker
          value={values.event_timezone}
          onChange={(tz) => f.setFieldValue("event_timezone", tz, false)}
        />
      </View>

      {onNoDate && <NoDateYetLink onPress={onNoDate} />}
    </>
  );
};

// The way out for hosts without a date, in the title step's link style, on
// one line. No event is created: they sign up, try a free test and schedule
// later.
export const NoDateYetLink = ({ onPress }) => (
  <View style={tw`flex-row flex-wrap items-center mt-2.5`}>
    <Text>{t("Don’t have a date yet?")} </Text>
    <TextLink text={t("Try a free test first")} onPress={onPress} />
  </View>
);
